import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit, Check, X, AlertCircle } from 'lucide-react';
import { Product } from '../../types';

export const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const fetchProducts = async () => {
    try {
      const res = await fetch('/api/admin/products');
      if (res.ok) {
        const data = await res.json() as any;
        setProducts(data.products || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct) return;

    try {
      const res = await fetch('/api/admin/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingProduct)
      });

      if (res.ok) {
        setMessage('Produit enregistré avec succès.');
        setEditingProduct(null);
        fetchProducts();
        setTimeout(() => setMessage(null), 3000);
      }
    } catch {
      alert('Erreur lors de l’enregistrement.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Gestion des Produits</h2>
          <p className="text-xs text-gray-500">Configurez vos articles, prix CHF/EUR et stocks réels</p>
        </div>
        <button
          onClick={() =>
            setEditingProduct({
              sku: 'FTS-MUG-NEW',
              slug: 'new-mug',
              name_fr: '',
              name_de: '',
              description_fr: '',
              description_de: '',
              price_chf: 24.90,
              price_eur: 24.90,
              is_active: 1,
              stock_quantity: 50,
              track_stock: 1
            })
          }
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-neutral-900 text-white rounded-xl text-xs font-bold hover:bg-pink-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nouveau produit</span>
        </button>
      </div>

      {message && (
        <div className="p-3 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-semibold">
          {message}
        </div>
      )}

      {/* Edit modal / drawer */}
      {editingProduct && (
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center border-b pb-3">
            <h3 className="text-sm font-bold text-gray-900">
              {editingProduct.id ? 'Modifier le produit' : 'Créer un produit'}
            </h3>
            <button onClick={() => setEditingProduct(null)} className="text-gray-400 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>

          <form onSubmit={handleSave} className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div>
              <label className="block font-semibold mb-1">SKU</label>
              <input
                type="text"
                required
                value={editingProduct.sku || ''}
                onChange={e => setEditingProduct({ ...editingProduct, sku: e.target.value })}
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">Slug</label>
              <input
                type="text"
                required
                value={editingProduct.slug || ''}
                onChange={e => setEditingProduct({ ...editingProduct, slug: e.target.value })}
                className="w-full p-2 border rounded-lg"
              />
            </div>

            <div>
              <label className="block font-semibold mb-1">Nom (FR)</label>
              <input
                type="text"
                required
                value={editingProduct.name_fr || ''}
                onChange={e => setEditingProduct({ ...editingProduct, name_fr: e.target.value })}
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">Nom (DE)</label>
              <input
                type="text"
                required
                value={editingProduct.name_de || ''}
                onChange={e => setEditingProduct({ ...editingProduct, name_de: e.target.value })}
                className="w-full p-2 border rounded-lg"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block font-semibold mb-1">Description (FR)</label>
              <textarea
                rows={3}
                value={editingProduct.description_fr || ''}
                onChange={e => setEditingProduct({ ...editingProduct, description_fr: e.target.value })}
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block font-semibold mb-1">Description (DE)</label>
              <textarea
                rows={3}
                value={editingProduct.description_de || ''}
                onChange={e => setEditingProduct({ ...editingProduct, description_de: e.target.value })}
                className="w-full p-2 border rounded-lg"
              />
            </div>

            <div>
              <label className="block font-semibold mb-1">Prix CHF</label>
              <input
                type="number"
                step="0.10"
                required
                value={editingProduct.price_chf || 0}
                onChange={e => setEditingProduct({ ...editingProduct, price_chf: parseFloat(e.target.value) })}
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">Prix EUR</label>
              <input
                type="number"
                step="0.10"
                required
                value={editingProduct.price_eur || 0}
                onChange={e => setEditingProduct({ ...editingProduct, price_eur: parseFloat(e.target.value) })}
                className="w-full p-2 border rounded-lg"
              />
            </div>

            <div>
              <label className="block font-semibold mb-1">Stock Réel</label>
              <input
                type="number"
                required
                value={editingProduct.stock_quantity || 0}
                onChange={e => setEditingProduct({ ...editingProduct, stock_quantity: parseInt(e.target.value, 10) })}
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div className="flex items-center gap-4 pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingProduct.is_active === 1}
                  onChange={e => setEditingProduct({ ...editingProduct, is_active: e.target.checked ? 1 : 0 })}
                  className="rounded text-pink-600"
                />
                <span className="font-semibold">Produit Actif</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingProduct.track_stock === 1}
                  onChange={e => setEditingProduct({ ...editingProduct, track_stock: e.target.checked ? 1 : 0 })}
                  className="rounded text-pink-600"
                />
                <span className="font-semibold">Suivre le Stock</span>
              </label>
            </div>

            <div className="sm:col-span-2 pt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingProduct(null)}
                className="px-4 py-2 border rounded-xl font-bold"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-neutral-900 text-white rounded-xl font-bold hover:bg-pink-600 transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full text-left text-xs">
          <thead className="bg-gray-50 border-b text-gray-500 font-semibold uppercase tracking-wider">
            <tr>
              <th className="p-4">SKU</th>
              <th className="p-4">Nom</th>
              <th className="p-4">Prix CHF / EUR</th>
              <th className="p-4">Stock</th>
              <th className="p-4">Statut</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {products.map(p => (
              <tr key={p.id} className="hover:bg-gray-50/50">
                <td className="p-4 font-mono font-bold">{p.sku}</td>
                <td className="p-4 font-medium text-gray-900">{p.name_fr}</td>
                <td className="p-4 font-mono">
                  {p.price_chf.toFixed(2)} CHF / {p.price_eur.toFixed(2)} EUR
                </td>
                <td className="p-4">
                  {p.track_stock === 1 ? (
                    <span className={`font-semibold ${p.stock_quantity <= 5 ? 'text-red-500' : 'text-gray-700'}`}>
                      {p.stock_quantity} unités
                    </span>
                  ) : (
                    <span className="text-gray-400">Non suivi</span>
                  )}
                </td>
                <td className="p-4">
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${
                      p.is_active === 1 ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {p.is_active === 1 ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <button
                    onClick={() => setEditingProduct(p)}
                    className="p-1.5 text-gray-600 hover:text-pink-600 rounded-lg hover:bg-gray-100"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
